import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Field, FormActions, Input, Textarea } from '@/app/components/ui/Field';
import { Modal } from '@/app/components/ui/Modal';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * Company admin (or admin): POST /companies/{id}/departments. Reached both
 * from Beheer (`/manage/companies/:companyId`) and from the Departments page
 * (`/departments`, itself reached via Instellingen › Locaties) — each caller
 * passes `state.from` on its Link so Cancel and a successful save return to
 * wherever "Nieuwe afdeling" was actually clicked, instead of always landing
 * on the newly created department's detail page.
 */
export function DepartmentNewPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    (location.state as { from?: string } | null)?.from ?? `/manage/companies/${companyId}`;
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const form = useSubmit(async () => {
    await apiPost('/companies/{companyId}/departments', {
      params: { companyId },
      body: {
        name,
        ...(category.trim() !== '' ? { category } : {}),
        ...(type.trim() !== '' ? { type } : {}),
      },
    });
    await navigate(returnTo);
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('departments.form.newTitle')} lead={t('departments.form.newLead')} />
      <Card className="max-w-2xl">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
        >
          <Field label={t('departments.form.name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label={t('departments.form.category')}>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} />
          </Field>
          <Field label={t('departments.form.type')}>
            <Input value={type} onChange={(e) => setType(e.target.value)} />
          </Field>
          <SubmitError error={form.error} />
          <FormActions>
            <Button as={Link} to={returnTo} variant="ghost">
              {t('departments.form.cancel')}
            </Button>
            <Button type="submit" disabled={form.pending || name.trim() === ''}>
              {t('departments.form.create')}
            </Button>
          </FormActions>
        </form>
      </Card>
    </section>
  );
}

/** Department admin or the company admin above: PUT /departments/{id}; delete lives here too. */
export function DepartmentEditPage() {
  const { t } = useTranslation();
  const { departmentId = '' } = useParams();
  const navigate = useNavigate();
  const department = useAsync(
    () => apiGet('/departments/{departmentId}', { params: { departmentId } }),
    [departmentId],
  );
  const [fields, setFields] = useState<{ name: string; category: string; type: string } | null>(
    null,
  );
  const save = useSubmit(async (values: { name: string; category: string; type: string }) => {
    await apiPut('/departments/{departmentId}', {
      params: { departmentId },
      body: {
        name: values.name,
        category: values.category.trim() === '' ? null : values.category,
        type: values.type.trim() === '' ? null : values.type,
      },
    });
    await navigate(`/departments/${departmentId}`);
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useSubmit(async (companyId: string | null) => {
    await apiDelete('/departments/{departmentId}', { params: { departmentId } });
    await navigate(companyId ? `/manage/companies/${companyId}` : '/manage/companies');
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('departments.form.editTitle')} />
      <AsyncSection state={department} label={t('departments.detail.thisDepartment')}>
        {(data) => {
          const current = fields ?? {
            name: data.name ?? '',
            category: data.category ?? '',
            type: data.type ?? '',
          };
          return (
            <>
              <Card className="max-w-2xl">
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void save.submit(current);
                  }}
                >
                  <Field label={t('departments.form.name')}>
                    <Input
                      value={current.name}
                      onChange={(e) => setFields({ ...current, name: e.target.value })}
                      required
                    />
                  </Field>
                  <Field label={t('departments.form.category')}>
                    <Input
                      value={current.category}
                      onChange={(e) => setFields({ ...current, category: e.target.value })}
                    />
                  </Field>
                  <Field label={t('departments.form.type')}>
                    <Input
                      value={current.type}
                      onChange={(e) => setFields({ ...current, type: e.target.value })}
                    />
                  </Field>
                  <SubmitError error={save.error} />
                  <FormActions>
                    <Button as={Link} to={`/departments/${departmentId}`} variant="ghost">
                      {t('departments.form.cancel')}
                    </Button>
                    <Button type="submit" disabled={save.pending || current.name.trim() === ''}>
                      {t('departments.form.save')}
                    </Button>
                  </FormActions>
                </form>
              </Card>

              <Card className="max-w-2xl" aria-label={t('departments.form.deleteCardTitle')}>
                <CardHeader title={t('departments.form.deleteCardTitle')} />
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  {t('departments.form.deleteButton')}
                </Button>
                <SubmitError error={remove.error} />
                <Modal
                  open={confirmDelete}
                  onClose={() => setConfirmDelete(false)}
                  title={t('departments.form.deleteButton')}
                >
                  <p className="text-ink text-sm">
                    {t('departments.form.deleteConfirm', { name: data.name ?? '' })}
                  </p>
                  <FormActions className="mt-4">
                    <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                      {t('departments.form.deleteKeep')}
                    </Button>
                    <Button
                      variant="danger"
                      disabled={remove.pending}
                      onClick={() => void remove.submit(data.company_id ?? null)}
                    >
                      {t('departments.form.deleteYes')}
                    </Button>
                  </FormActions>
                </Modal>
              </Card>
            </>
          );
        }}
      </AsyncSection>
    </section>
  );
}

/** Admin only — not the company admin, not the coach: PUT /departments/{id}/visibility. */
export function DepartmentVisibilityPage() {
  const { t } = useTranslation();
  const { departmentId = '' } = useParams();
  const navigate = useNavigate();
  const department = useAsync(
    () => apiGet('/departments/{departmentId}', { params: { departmentId } }),
    [departmentId],
  );
  const [choice, setChoice] = useState<'aggregate' | 'individual' | null>(null);
  const form = useSubmit(async (value: 'aggregate' | 'individual') => {
    await apiPut('/departments/{departmentId}/visibility', {
      params: { departmentId },
      body: { stats_visibility: value },
    });
    await navigate(`/departments/${departmentId}`);
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('departments.form.visibilityTitle')} />
      <AsyncSection state={department} label={t('departments.detail.thisDepartment')}>
        {(data) => {
          const current = choice ?? data.stats_visibility ?? 'aggregate';
          return (
            <Card className="max-w-2xl">
              <form
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void form.submit(current);
                }}
              >
                <Alert tone="info">{t('departments.form.visibilityLead')}</Alert>
                <label className="text-ink flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="visibility"
                    className="mt-1"
                    checked={current === 'aggregate'}
                    onChange={() => setChoice('aggregate')}
                  />
                  <span>{t('departments.form.visibilityAggregate')}</span>
                </label>
                <label className="text-ink flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="visibility"
                    className="mt-1"
                    checked={current === 'individual'}
                    onChange={() => setChoice('individual')}
                  />
                  <span>{t('departments.form.visibilityIndividual')}</span>
                </label>
                <SubmitError error={form.error} />
                <FormActions>
                  <Button as={Link} to={`/departments/${departmentId}`} variant="ghost">
                    {t('departments.form.cancel')}
                  </Button>
                  <Button type="submit" disabled={form.pending}>
                    {t('departments.form.save')}
                  </Button>
                </FormActions>
              </form>
            </Card>
          );
        }}
      </AsyncSection>
    </section>
  );
}

/** Admin only: record (PUT, with a note) or revoke (DELETE) the collective basis. */
export function DepartmentCollectiveConsentPage() {
  const { t } = useTranslation();
  const { departmentId = '' } = useParams();
  const navigate = useNavigate();
  const department = useAsync(
    () => apiGet('/departments/{departmentId}', { params: { departmentId } }),
    [departmentId],
  );
  const [note, setNote] = useState('');
  const record = useSubmit(async () => {
    await apiPut('/departments/{departmentId}/collective-consent', {
      params: { departmentId },
      body: { note },
    });
    await navigate(`/departments/${departmentId}`);
  });
  const revoke = useSubmit(async () => {
    await apiDelete('/departments/{departmentId}/collective-consent', {
      params: { departmentId },
    });
    await navigate(`/departments/${departmentId}`);
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('departments.form.collectiveConsentTitle')} />
      <AsyncSection state={department} label={t('departments.detail.thisDepartment')}>
        {(data) => (
          <>
            <Card className="max-w-2xl">
              <Alert tone="info">
                {t('departments.form.collectiveConsentLead', { name: data.name ?? '' })}
              </Alert>
              <form
                className="mt-4 flex flex-col gap-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void record.submit();
                }}
              >
                <Field label={t('departments.form.collectiveConsentNote')}>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
                </Field>
                <SubmitError error={record.error} />
                <FormActions>
                  <Button as={Link} to={`/departments/${departmentId}`} variant="ghost">
                    {t('departments.form.cancel')}
                  </Button>
                  <Button type="submit" disabled={record.pending || note.trim() === ''}>
                    {t('departments.form.collectiveConsentRecord')}
                  </Button>
                </FormActions>
              </form>
            </Card>

            {data.collective_consent && (
              <Card className="max-w-2xl" aria-label={t('departments.form.revokeCardTitle')}>
                <CardHeader title={t('departments.form.revokeCardTitle')} />
                <Button
                  variant="danger"
                  disabled={revoke.pending}
                  onClick={() => void revoke.submit()}
                >
                  {t('departments.form.revokeButton')}
                </Button>
                <SubmitError error={revoke.error} />
              </Card>
            )}
          </>
        )}
      </AsyncSection>
    </section>
  );
}
